import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface Props {
  children: ReactNode;
  /** Changes whenever the active option changes, so it is scrolled into view. */
  activeKey?: string;
  /** Role and accessible name of the option row itself (the arrows sit outside it). */
  role?: string;
  label?: string;
  /** id of an element (e.g. an inline error) that describes the option row. */
  describedBy?: string;
  rowClassName?: string;
  className?: string;
  /** Shrink-wrap instead of filling the line. */
  inline?: boolean;
}

const ARROW = 'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] border border-line-strong bg-surface text-navy transition-colors hover:border-line-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:pointer-events-none disabled:opacity-40 dark:text-ink';

/**
 * Single non-wrapping row inside an overflow-hidden scroller, with prev/next
 * arrows that appear only when the content overflows.
 */
export default function OverflowRow({ children, activeKey, role, label, describedBy, rowClassName = '', className = '', inline = false }: Props) {
  const scroller = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState(false);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(true);
  const mounted = useRef(false);

  const measure = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    setOverflow(el.scrollWidth > el.clientWidth + 1);
    setAtStart(el.scrollLeft <= 0);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    measure();
    const el = scroller.current;
    if (!el) return;
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(el);
      if (el.firstElementChild) ro.observe(el.firstElementChild);
    } else {
      window.addEventListener('resize', measure);
    }
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [measure]);

  useEffect(() => {
    const el = scroller.current;
    // Pressed pills and selected tabs (tabs mode) alike are brought into view.
    const active = el?.querySelector<HTMLElement>('[aria-pressed="true"],[aria-selected="true"]');
    if (el && active && typeof el.scrollTo === 'function') {
      const box = el.getBoundingClientRect();
      const item = active.getBoundingClientRect();
      const left = el.scrollLeft + (item.left - box.left) - (el.clientWidth - item.width) / 2;
      el.scrollTo({ left: Math.max(0, left), behavior: mounted.current ? 'smooth' : 'auto' });
    }
    mounted.current = true;
    measure();
  }, [activeKey, measure]);

  const page = (dir: -1 | 1) => {
    const el = scroller.current;
    if (!el) return;
    const left = dir * Math.max(el.clientWidth * 0.8, 1);
    if (typeof el.scrollBy === 'function') el.scrollBy({ left, behavior: 'smooth' });
    else el.scrollLeft += left;
  };

  return (
    <div className={`${inline ? 'inline-flex' : 'flex'} max-w-full items-center gap-2 ${className}`}>
      {overflow && (
        <button type="button" aria-label="Show previous options" disabled={atStart} onClick={() => page(-1)} className={ARROW}>
          <ChevronLeft size={16} strokeWidth={1.8} aria-hidden="true" />
        </button>
      )}
      <div ref={scroller} onScroll={measure} className="min-w-0 flex-1 overflow-hidden">
        <div role={role} aria-label={label} aria-describedby={describedBy} aria-invalid={describedBy ? true : undefined} className={`flex w-max flex-nowrap ${rowClassName}`}>{children}</div>
      </div>
      {overflow && (
        <button type="button" aria-label="Show next options" disabled={atEnd} onClick={() => page(1)} className={ARROW}>
          <ChevronRight size={16} strokeWidth={1.8} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
